/**
 * Utility functions for handling app store redirections
 */

/**
 * Detects the user's device type
 * @returns {string} 'ios', 'android', or 'desktop'
 */
export const detectDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  console.log("User Agent:", navigator);
  const platform = navigator.platform || "";

  // iOS detection - check both userAgent and platform
  if (
    (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) ||
    (/Mac|iPhone|iPad|iPod/.test(platform) && "ontouchend" in document)
  ) {
    return "ios";
  }

  // Android detection - must be on a mobile device, not desktop
  if (/android/i.test(userAgent) && /mobile/i.test(userAgent)) {
    return "android";
  }

  // Mac detection
  if (
    /Macintosh|MacIntel|MacPPC|Mac68K/.test(platform) ||
    /Mac OS X/.test(userAgent)
  ) {
    return "desktop";
  }

  // Windows detection
  if (/Win32|Win64|Windows|WinCE/.test(platform)) {
    return "desktop";
  }

  // Linux detection
  if (/Linux/.test(platform) && !/android/i.test(userAgent)) {
    return "desktop";
  }

  // Desktop fallback
  return "desktop";
};

/**
 * Redirects to the appropriate app store based on device type
 * @param {object} settings - Settings object containing AppleStoreLink and PlayStoreLink
 * @param {string} fallbackUrl - Fallback URL if no app store links are available (optional)
 * @param {string} appType - Type of app: 'customer' or 'driver' (optional, defaults to 'customer')
 */
export const redirectToAppStore = (
  settings,
  fallbackUrl = "/login?signup=true",
  appType = "customer"
) => {
  const deviceType = detectDeviceType();

  // Primary Google Play Store link for HARAKA
  const harakaPlayStoreLink =
    "https://play.google.com/store/apps/details?id=com.harakaride.app";

  // Get app store links from settings - check for driver or customer links
  const appleLink =
    appType === "driver"
      ? settings?.DriverAppleStoreLink ||
        "https://apps.apple.com/ng/app/haraka-ride/id6753879505"
      : settings?.AppleStoreLink ||
        "https://apps.apple.com/ng/app/haraka-ride/id6753879505";

  const playLink =
    appType === "driver"
      ? settings?.DriverPlayStoreLink || harakaPlayStoreLink
      : settings?.PlayStoreLink || harakaPlayStoreLink;

  console.log("Device type detected:", deviceType);

  switch (deviceType) {
    case "ios":
      window.open(appleLink, "_blank");
      break;

    case "android":
      window.open(playLink, "_blank");
      break;

    default:
      // For desktop, prioritize Play Store link
      window.open(playLink, "_blank");
      break;
  }
};

/**
 * Gets the appropriate app store URL based on device type
 * @param {object} settings - Settings object containing AppleStoreLink and PlayStoreLink
 * @param {string} fallbackUrl - Fallback URL if no app store links are available
 * @param {string} appType - Type of app: 'customer' or 'driver' (optional, defaults to 'customer')
 * @returns {string} The appropriate app store URL
 */
export const getAppStoreUrl = (
  settings,
  fallbackUrl = "/login?signup=true",
  appType = "customer"
) => {
  const deviceType = detectDeviceType();

  // Primary Google Play Store link for HARAKA
  const harakaPlayStoreLink =
    "https://play.google.com/store/apps/details?id=com.harakaride.app";

  // Get app store links from settings
  const appleLink = settings?.DriverAppleStoreLink || settings?.AppleStoreLink;

  switch (deviceType) {
    case "ios":
      return appleLink || harakaPlayStoreLink;

    case "android":
      return harakaPlayStoreLink;

    default:
      return harakaPlayStoreLink;
  }
};

/**
 * Handles click event for app store buttons
 * @param {object} settings - Settings object containing AppleStoreLink and PlayStoreLink
 * @param {string} fallbackUrl - Fallback URL if no app store links are available
 * @param {string} appType - Type of app: 'customer' or 'driver' (optional, defaults to 'customer')
 * @returns {function} Click handler function
 */
export const handleAppStoreClick = (
  settings,
  fallbackUrl = "/login?signup=true",
  appType = "customer"
) => {
  return (event) => {
    event.preventDefault();
    redirectToAppStore(settings, fallbackUrl, appType);
  };
};
